import { api_service } from './api';

/**
 * Authentication service
 * Handles user login, logout, registration, and token management
 */

interface LoginCredentials {
  username: string;
  password: string;
}

interface RegisterData {
  username?: string;
  email: string;
  password: string;
  password_confirm: string;
  first_name: string;
  last_name: string;
}

interface AuthResponse {
  access: string;
  refresh: string;
  user: User;
}

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'operator' | 'manager';
  is_active: boolean;
  date_joined: string;
}

interface PasswordChangeData {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
}

interface PasswordResetRequest {
  email: string;
}

interface PasswordResetConfirm {
  token: string;
  password: string;
  password_confirm: string;
}

// API Error interface for better error handling
interface ApiError {
  message?: string;
  errors?: Record<string, string[]>;
  detail?: string;
}

class AuthService {
  private readonly TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'user_data';
  
  private readonly API_BASE = '/accounts';

  /**
   * Login user with credentials
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      console.log('Attempting login with:', { username: credentials.username });
      
      const response = await api_service.post<AuthResponse>(
        `${this.API_BASE}/login/`, 
        credentials
      );
      
      console.log('Login successful, response:', response);
      
      // Store tokens and user data
      this.set_tokens(response.access, response.refresh);
      this.set_user(response.user);
      
      return response;
    } catch (error: any) {
      // Log the full error object for debugging
      console.error('Login error - Full error:', error);
      console.error('Login error details:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: error?.message,
        config: error?.config
      });
      
      // Extract and format error message
      const apiError = error?.response?.data as ApiError;
      let errorMessage = 'An error occurred during login';
      
      if (apiError) {
        // Check for various error formats
        if (apiError.detail) {
          errorMessage = apiError.detail;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        } else if (apiError.errors) {
          // Format validation errors
          const errorMessages = Object.entries(apiError.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('; ');
          errorMessage = errorMessages || errorMessage;
        } else {
          // If data exists but doesn't match expected format, stringify it
          errorMessage = JSON.stringify(apiError);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      console.log('Attempting registration with:', { 
        username: data.username, 
        email: data.email 
      });
      
      const response = await api_service.post<AuthResponse>(
        `${this.API_BASE}/register/`, 
        data
      );
      
      console.log('Registration successful');
      
      // Store tokens and user data
      this.set_tokens(response.access, response.refresh);
      this.set_user(response.user);
      
      return response;
    } catch (error: any) {
      console.error('Registration error details:', {
        status: error?.response?.status,
        data: error?.response?.data
      });
      
      const apiError = error?.response?.data as ApiError;
      let errorMessage = 'An error occurred during registration';
      
      if (apiError?.errors) {
        const errorMessages = Object.entries(apiError.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('; ');
        errorMessage = errorMessages || errorMessage;
      } else if (apiError?.detail) {
        errorMessage = apiError.detail;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Logout user - clear tokens and user data
   */
  async logout(): Promise<void> {
    try {
      const refresh_token = this.get_refresh_token();
      if (refresh_token) {
        await api_service.post(`${this.API_BASE}/logout/`, { 
          refresh: refresh_token 
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clear_auth_data();
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refresh_token(): Promise<string> {
    try {
      const refresh_token = this.get_refresh_token();
      
      if (!refresh_token) {
        throw new Error('No refresh token available');
      }

      const response = await api_service.post<{ access: string }>(
        `${this.API_BASE}/token/refresh/`, 
        { refresh: refresh_token }
      );

      this.set_access_token(response.access);
      return response.access;
    } catch (error: any) {
      console.error('Token refresh error:', error);
      this.clear_auth_data();
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.message || 
                          'Failed to refresh token';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get current user profile
   */
  async get_current_user(): Promise<User> {
    try {
      const response = await api_service.get<User>(`${this.API_BASE}/user/`);
      this.set_user(response);
      return response;
    } catch (error: any) {
      console.error('Get user error:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.message || 
                          'Failed to fetch user data';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update user profile
   */
  async update_profile(data: Partial<User>): Promise<User> {
    try {
      const response = await api_service.patch<User>(
        `${this.API_BASE}/user/`, 
        data
      );
      this.set_user(response);
      return response;
    } catch (error: any) {
      console.error('Update profile error:', error);
      
      const apiError = error?.response?.data as ApiError;
      let errorMessage = 'Failed to update profile';
      
      if (apiError?.errors) {
        const errorMessages = Object.entries(apiError.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('; ');
        errorMessage = errorMessages;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Change user password
   */
  async change_password(data: PasswordChangeData): Promise<{ message: string }> {
    try {
      return await api_service.post(`${this.API_BASE}/password/change/`, data);
    } catch (error: any) {
      console.error('Password change error:', error);
      
      const apiError = error?.response?.data as ApiError;
      let errorMessage = 'Failed to change password';
      
      if (apiError?.errors) {
        const errorMessages = Object.entries(apiError.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('; ');
        errorMessage = errorMessages;
      } else if (apiError?.detail) {
        errorMessage = apiError.detail;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Request password reset
   */
  async request_password_reset(data: PasswordResetRequest): Promise<{ message: string }> {
    try {
      return await api_service.post(`${this.API_BASE}/password/reset/`, data);
    } catch (error: any) {
      console.error('Password reset request error:', error);
      throw new Error(error?.response?.data?.detail || 'Failed to request password reset');
    }
  }

  /**
   * Confirm password reset with token
   */
  async confirm_password_reset(data: PasswordResetConfirm): Promise<{ message: string }> {
    try {
      return await api_service.post(`${this.API_BASE}/password/reset/confirm/`, data);
    } catch (error: any) {
      console.error('Password reset confirm error:', error);
      throw new Error(error?.response?.data?.detail || 'Failed to reset password');
    }
  }

  /**
   * Verify if user is authenticated
   */
  is_authenticated(): boolean {
    return !!this.get_access_token();
  }

  /**
   * Get stored access token
   */
  get_access_token(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get stored refresh token
   */
  get_refresh_token(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Get stored user data
   */
  get_user(): User | null {
    const user_data = localStorage.getItem(this.USER_KEY);
    return user_data ? JSON.parse(user_data) : null;
  }

  /**
   * Set access token
   */
  private set_access_token(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Set refresh token
   */
  private set_refresh_token(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  /**
   * Set both tokens
   */
  private set_tokens(access: string, refresh: string): void {
    this.set_access_token(access);
    this.set_refresh_token(refresh);
  }

  /**
   * Set user data in localStorage
   */
  private set_user(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Clear all authentication data
   */
  private clear_auth_data(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  /**
   * Check if user has specific role
   */
  has_role(role: User['role']): boolean {
    const user = this.get_user();
    return user?.role === role;
  }

  /**
   * Check if user has any of the specified roles
   */
  has_any_role(roles: User['role'][]): boolean {
    const user = this.get_user();
    return user ? roles.includes(user.role) : false;
  }
}

// Export singleton instance
export const auth_service = new AuthService();

// Export types
export type {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  User,
  PasswordChangeData,
  PasswordResetRequest,
  PasswordResetConfirm,
};